import React, { useState, useEffect } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Loader2, BarChart3, UserCheck, Users, MessageCircle } from "lucide-react";
import { getAnalyticsData } from "@/utils/admin/analytics";
import { getAllClients } from "@/utils/admin/clients";
import { getAllTherapists } from "@/utils/admin/therapists";
import UsersList from "./UsersList";
import DashboardContent from "./dashboard/DashboardContent";
import { supabase } from "@/integrations/supabase/client";
import FeedbackTable from "./FeedbackTable";
const AnalyticsDashboard = ({ feedbackCount }: { feedbackCount: number | null }) => {
  const [analyticsData, setAnalyticsData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [clientsData, setClientsData] = useState<any[]>([]);
  const [therapistsData, setTherapistsData] = useState<any[]>([]);
  const [loadingLists, setLoadingLists] = useState(true);
  const [activeTab, setActiveTab] = useState("dashboard");
  useEffect(() => {
    const fetchAnalytics = async () => {
      try {
        setLoading(true);
        console.log("Fetching analytics data...");
        const data = await getAnalyticsData();
        console.log("Analytics data received:", data);
        setAnalyticsData(data);
      } catch (error) {
        console.error("Error loading analytics:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchAnalytics();

    // Set up a listener for authentication state changes to refresh data
    const {
      data: {
        subscription
      }
    } = supabase.auth.onAuthStateChange(event => {
      if (event === 'SIGNED_IN' || event === 'SIGNED_OUT') {
        console.log("Auth state changed, refreshing analytics data...");
        fetchAnalytics();
      }
    });
    return () => {
      subscription.unsubscribe();
    };
  }, []);
  useEffect(() => {
    const fetchUserLists = async () => {
      if (activeTab === "clients" || activeTab === "therapists") {
        setLoadingLists(true);
        try {
          if (activeTab === "clients") {
            console.log("Fetching clients data using secure function...");
            const clients = await getAllClients();
            console.log("Clients data received:", clients?.length);
            setClientsData(clients || []);
          } else if (activeTab === "therapists") {
            console.log("Fetching therapists data...");
            const therapists = await getAllTherapists();
            console.log("Therapists data received:", therapists?.length);
            setTherapistsData(therapists || []);
          }
        } catch (error) {
          console.error(`Error loading ${activeTab}:`, error);
        } finally {
          setLoadingLists(false);
        }
      }
    };
    fetchUserLists();
  }, [activeTab]);
  if (loading) {
    return <div className="flex justify-center items-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-[#3BAFDA]" />
        <p className="ml-2 text-[#5A636E]">Loading analytics data...</p>
      </div>;
  }
  if (!analyticsData) {
    return <div className="text-center p-8">
        <p className="text-red-500">Error loading analytics data.</p>
      </div>;
  }
  return <div className="space-y-6 w-full">
      <div className="flex flex-col w-full">
        <Tabs defaultValue="dashboard" value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="mb-4">
            <TabsTrigger value="dashboard" className="flex items-center gap-2">
              <BarChart3 className="h-4 w-4" />
              Dashboard
            </TabsTrigger>
            <TabsTrigger value="clients" className="flex items-center gap-2">
              <UserCheck className="h-4 w-4" />
              All Clients ({analyticsData.clientCount || 0})
            </TabsTrigger>
            <TabsTrigger value="therapists" className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              All Therapists ({analyticsData.therapistCount || 0})
            </TabsTrigger>
            <TabsTrigger value="feedback" className="flex items-center gap-2">
              <MessageCircle className="h-4 w-4 mr-1" />
              All Feedback{typeof feedbackCount === 'number' ? ` (${feedbackCount})` : ''}
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="dashboard" className="w-full">
            <DashboardContent analyticsData={analyticsData} />
          </TabsContent>

          <TabsContent value="clients" className="w-full">
            {loadingLists ? <div className="flex justify-center items-center p-8">
                <Loader2 className="h-8 w-8 animate-spin text-[#3BAFDA]" />
                <p className="ml-2 text-[#5A636E]">Loading clients data...</p>
              </div> : <UsersList title="All Clients" users={clientsData} itemsPerPage={10} onUserDeleted={() => {
            // Refresh the data after a user is deleted
            setLoading(true);
            setActiveTab("dashboard");
            getAnalyticsData().then(data => {
              setAnalyticsData(data);
              setLoading(false);
            }).catch(error => {
              console.error("Error refreshing analytics after deletion:", error);
              setLoading(false);
            });
          }} />}
          </TabsContent>

          <TabsContent value="therapists" className="w-full">
            {loadingLists ? <div className="flex justify-center items-center p-8">
                <Loader2 className="h-8 w-8 animate-spin text-[#3BAFDA]" />
                <p className="ml-2 text-[#5A636E]">Loading therapists data...</p>
              </div> : <UsersList title="All Therapists" users={therapistsData} itemsPerPage={10} onUserDeleted={() => {
            // Refresh the data after a user is deleted
            setLoading(true);
            setActiveTab("dashboard");
            getAnalyticsData().then(data => {
              setAnalyticsData(data);
              setLoading(false);
            }).catch(error => {
              console.error("Error refreshing analytics after deletion:", error);
              setLoading(false);
            });
          }} />}
          </TabsContent>

          <TabsContent value="feedback" className="w-full">
            <FeedbackTable />
          </TabsContent>
        </Tabs>
        <div className="text-xs text-[#5A636E] text-right mt-4">Coded by Lou Schillaci</div>
      </div>
    </div>;
};
export default AnalyticsDashboard;